import { useEffect } from 'react';
import { formatCourt } from '../format';
import type { CommandeListee } from '../api/types';

/**
 * Avertissement avant une demande d'avis qui part TOUT DE SUITE.
 *
 * Cocher la case programme normalement l'envoi pour trois jours après la
 * livraison : le geste est sans conséquence immédiate, et se défait d'un
 * second clic. Sur une commande livrée depuis plus longtemps, l'échéance est
 * déjà dans le passé — cocher déclenche alors une sollicitation qui part
 * séance tenante, chez un vrai client, et ne se rattrape pas.
 *
 * Deux gestes identiques à l'écran, deux conséquences opposées : c'est
 * exactement le cas où une confirmation se justifie. Elle dit ce qui va se
 * produire, et pourquoi ici et pas ailleurs.
 */
export function ConfirmerAvisImmediat({
  commande, enAttente, onConfirmer, onFermer,
}: {
  commande: CommandeListee;
  enAttente: boolean;
  onConfirmer: () => void;
  onFermer: () => void;
}) {
  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [onFermer]);

  return (
    <div className="modal" onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <div
        className="box" role="dialog" aria-modal="true"
        aria-label={`Demander un avis pour ${commande.ref} maintenant ?`}
        style={{ borderTopColor: 'var(--brick)' }}
      >
        <div className="mhead">
          <h2>Demander un avis maintenant ?</h2>
          <button className="x" onClick={onFermer} aria-label="Fermer">&times;</button>
        </div>

        <div className="mbody">
          <p className="err" style={{ marginBottom: 14 }}>
            La demande partira <strong>immédiatement</strong>, et non dans trois jours : la
            commande a été livrée le {formatCourt(commande.dateStatutTransporteur)}, le délai
            d&apos;attente est donc déjà écoulé.
          </p>

          <dl className="kv" style={{ marginBottom: 14 }}>
            <dt>Client</dt><dd style={{ textAlign: 'left' }}>{commande.client}</dd>
            <dt>Adresse</dt>
            <dd style={{ textAlign: 'left' }}>{commande.email ?? 'aucune adresse connue'}</dd>
            <dt>Livrée le</dt><dd>{formatCourt(commande.dateStatutTransporteur)}</dd>
          </dl>

          <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: 0 }}>
            L&apos;appel part vers le scénario, qui écrit et envoie le message. Une fois parti, il
            ne se décoche plus.
          </p>
        </div>

        <div className="mfoot">
          <button type="button" className="btn" onClick={onFermer}>Annuler</button>
          <button
            type="button" className="btn pri" disabled={enAttente} onClick={onConfirmer}
          >
            {enAttente ? <><span className="spin" /> Envoi…</> : 'Envoyer maintenant'}
          </button>
        </div>
      </div>
    </div>
  );
}
